'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IFsNodeSchema } from "@cloudike/web_files"
import { Button } from 'antd'
import classNames from "classnames"
import { getDraggedItems } from 'features/common/model/selectors/dragSelectors'
import { endDrag, startDrag } from 'features/common/model/slice/dragSlice'
import { getFilesPreviewIsPublicLinkSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { useGetEnablePermission } from 'features/public-link-files/hooks/useGetEnablePermission'
import { MemberRole } from 'features/team/model/types/members'
import { getUserRole } from 'features/user/model/selectors/userSelectors'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { BlockOutlined, Dropdown, MenuProps, PlusCircleOutlined } from "shared/ui"
import { hasAccess } from 'shared/utils/access'
import { checkPermission } from 'shared/utils/checkPermission'
import styled from "styled-components"
import { BoardToolbarAction } from 'shared/constants/toolbar'

import {
  ArrowRightOutlined,
  ArrowsAltOutlined, CopyOutlined, DeleteOutlined, DownloadOutlined, EditOutlined,
  EllipsisOutlined, LinkOutlined
} from '../../../shared/ui'
import { useDoubleClick } from '../../common/hooks/useDoubleClick'
import { DragPreview } from '../../common/ui/DragPreview'

// Define a custom type for the menu click event with itemId
interface CustomMenuInfo {
  key: string;
  keyPath: string[];
  itemId?: string;
  domEvent: React.MouseEvent<HTMLElement>;
}

interface BoardsGridProps {
  boards: IFsNodeSchema[];
  selectedItemsIds?: string[];
  selectedAssetsIds?: string[];
  assets?: IFsNodeSchema[];
  onClickOutside?: () => void;
  onSelect?: (board: IFsNodeSchema) => void;
  onBoardDoubleClick?: (board: IFsNodeSchema) => void;
  onMenuItemClick?: (actionId: string, selectedBoardsIds: string) => void;
  contextMenuItems?: MenuProps['items'];
  selectedContainerId?: string;
  selectedTargetClassName?: string;
  isItemHoverEnabled?: boolean;
  onContextStateChanged: (isOpened: boolean) => void;
  isOuterContextMenuOpened: boolean;
  onAssetDropOnBoard?: (assetIds: string[], boardId: string) => void;
  onBoardDropOnBoard?: (boardIds: string[], targetBoardId: string) => void;
  onBoardDragStart?: (boardId: string) => void;
}

export const BoardsGrid = ({
  boards,
  selectedItemsIds,
  selectedAssetsIds = [],
  assets = [],
  onClickOutside,
  onSelect,
  onBoardDoubleClick,
  onMenuItemClick,
  contextMenuItems,
  selectedContainerId = 'boards-grid',
  selectedTargetClassName = 'js-board-card',
  isItemHoverEnabled = true,
  onContextStateChanged = () => undefined,
  isOuterContextMenuOpened = false,
  onAssetDropOnBoard,
  onBoardDropOnBoard,
  onBoardDragStart,
}: BoardsGridProps) => {
  const dispatch = useAppDispatch()
  const userRole = useAppSelector(getUserRole)
  const draggedItems = useAppSelector(getDraggedItems)
  const [openCtxDropdownId, setOpenCtxDropdownId] = useState<string | null>(null)
  const [openBtnDropdownId, setOpenBtnDropdownId] = useState<string | null>(null)
  const [dragOverBoardId, setDragOverBoardId] = useState<string | null>(null)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const { isDownloadEnabled, isEditContentEnabled } = useGetEnablePermission()
  const dragPreviewRef = useRef<HTMLDivElement>(null)

  const hideContextMenus = () => {
    setOpenCtxDropdownId(null)
    setOpenBtnDropdownId(null)
    onContextStateChanged(false)
  }


  useEffect(() => {
    if(selectedItemsIds.length > 0 || isOuterContextMenuOpened) {
      hideContextMenus()
    }
  }, [selectedItemsIds, isOuterContextMenuOpened])

  const onBoardCardClick = (_, item) => {
    hideContextMenus()
    onSelect(item)
  }

  const handleBoardDoubleClick = (_, item) => {
    hideContextMenus()
    onBoardDoubleClick(item)
  }

  const doubleClickCallback = useDoubleClick(onBoardCardClick, handleBoardDoubleClick )

  const handleBoardMousedownOutside = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLElement) {
      const target = event.target
      const isBoard = target.classList.contains(selectedTargetClassName)
      if (!isBoard && onClickOutside) {
        onClickOutside()
      }
    }
  }, [onClickOutside, selectedTargetClassName])

  useEffect(() => {
    document.addEventListener('mousedown', handleBoardMousedownOutside as unknown as EventListener)

    return () => {
      document.removeEventListener('mousedown', handleBoardMousedownOutside as unknown as EventListener)
    }
  }, [handleBoardMousedownOutside])


  const handleMenuClick = (data: CustomMenuInfo) => {
    hideContextMenus()
    onMenuItemClick(data.key, data.itemId || '')
  }

  const getMenuProps = (item: IFsNodeSchema) => {
    return {
      items: menuItems,
      onClick: (e) => {
        e.domEvent.stopPropagation()
        handleMenuClick({ ...e, itemId: item.id })
      },
      className: 'js-board-card-dropdown'
    }
  }

  const preventPropagation = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleDragOver = (e: React.DragEvent, boardId: string) => {
    e.preventDefault()

    if (selectedItemsIds?.includes(boardId) || draggedItems.includes(boardId)) {
      e.dataTransfer.dropEffect = 'none'
      return
    }

    e.dataTransfer.dropEffect = 'move'
    setDragOverBoardId(boardId)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverBoardId(null)
    }
  }

  const handleDrop = (e: React.DragEvent, boardId: string) => {
    e.preventDefault()
    setDragOverBoardId(null)

    if (selectedItemsIds?.includes(boardId) || draggedItems.includes(boardId)) {
      return
    }

    try {
      const dragData = e.dataTransfer.getData('application/json')
      if (dragData) {
        const { assets, boards } = JSON.parse(dragData)

        if (assets && assets.length > 0 && onAssetDropOnBoard) {
          onAssetDropOnBoard(assets, boardId)
        }

        if (boards && boards.length > 0 && onBoardDropOnBoard) {
          onBoardDropOnBoard(boards, boardId)
        }
      }
    } catch (error) {
      console.error('Error parsing drag data:', error)
    }
  }

  const handleBoardDragStart = (e: React.DragEvent, board: IFsNodeSchema) => {
    const isBoardSelected = selectedItemsIds?.includes(board.id)

    const draggedBoards = isBoardSelected
      ? selectedItemsIds.filter(id => boards.find(b => b.id === id))
      : [board.id]

    const draggedAssets = isBoardSelected && selectedAssetsIds.length > 0
      ? selectedAssetsIds.filter(id => assets.find(a => a.id === id))
      : []

    const allDraggedItems = [...draggedBoards, ...draggedAssets]
    dispatch(startDrag(allDraggedItems))

    e.dataTransfer.setData('text/plain', board.id)
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'mixed',
      id: board.id,
      assets: draggedAssets,
      boards: draggedBoards,
      name: board.name
    }))
    e.dataTransfer.effectAllowed = 'move'

    const dragPreviewElement = dragPreviewRef.current?.cloneNode(true) as HTMLElement

    if (dragPreviewElement) {
      const totalCount = draggedBoards.length + draggedAssets.length
      const textElement = dragPreviewElement.querySelector('.js-drag-preview-text') as HTMLElement
      const countElement = dragPreviewElement.querySelector('.js-drag-preview-count') as HTMLElement
      const layoutElement = dragPreviewElement.querySelector('.js-drag-preview-layout') as HTMLElement
      const gridIcon = dragPreviewElement.querySelector('.js-drag-preview-grid-icon') as HTMLElement
      const pictureIcon = dragPreviewElement.querySelector('.js-drag-preview-picture-icon') as HTMLElement

      textElement.textContent = board.name

      if (totalCount > 1) {
        countElement.textContent = totalCount.toString()
        countElement.style.display = 'flex'
        layoutElement.style.display = 'block'
      } else {
        countElement.style.display = 'none'

        layoutElement.style.display = 'none'
      }

      if (draggedBoards.length > 0) {
        pictureIcon.style.display = 'none'
        gridIcon.style.display = 'block'
      } else {
        pictureIcon.style.display = 'block'
        gridIcon.style.display = 'none'
      }

      dragPreviewElement.style.display = 'block'
      dragPreviewElement.style.boxShadow = 'none!important'
      dragPreviewElement.style.filter = 'none!important'
      dragPreviewElement.style.opacity = '1!important'

      document.body.appendChild(dragPreviewElement)
      e.dataTransfer.setDragImage(dragPreviewElement, 0, 0)

      setTimeout(() => {
        document.body.removeChild(dragPreviewElement)
      }, 0)
    }

    if (onBoardDragStart) {
      onBoardDragStart(board.id)
    }
  }

  const handleBoardDragEnd = () => {
    dispatch(endDrag())
  }

  const menuItems = useMemo(() => {
    if (contextMenuItems) {
      return contextMenuItems
    }

    return [
      {
        key: BoardToolbarAction.Open,
        label: 'Open',
        icon: <ArrowsAltOutlined />,
      },
      hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && !isPublicLink && {
        key: BoardToolbarAction.Share,
        label: 'Share link',
        icon: <LinkOutlined />,
      },
      {
        type: 'divider' as const,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.CreateSubBoard,
        label: 'Create sub-board',
        icon: <PlusCircleOutlined />,
      },
      isDownloadEnabled && {
        key: BoardToolbarAction.Download,
        label: 'Download',
        icon: <DownloadOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Copy,
        label: 'Copy to board',
        icon: <CopyOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Move,
        label: 'Move to board',
        icon: <ArrowRightOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Rename,
        label: 'Rename',
        icon: <EditOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Duplicate,
        label: 'Duplicate',
        icon: <BlockOutlined />,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], isEditContentEnabled, isPublicLink) && {
        type: 'divider' as const,
      },
      checkPermission(userRole, [MemberRole.ADMIN, MemberRole.OWNER], isEditContentEnabled, isPublicLink) && {
        key: BoardToolbarAction.Delete,
        label: 'Delete from library',
        icon: <DeleteOutlined />,
        danger: true,
      }
    ]
  }, [contextMenuItems, userRole, isPublicLink, isDownloadEnabled, isEditContentEnabled])

  return (
    <SBoardsGrid
      id={selectedContainerId}
    >
      <DragPreview
        ref={dragPreviewRef}
        hidden
      />

      {boards.map((board) => {
        const isDragOver = dragOverBoardId === board.id
        const isDragging = draggedItems.includes(board.id)

        return (
          <SBardCardWrapper
            key={board.id}
            className={classNames(selectedTargetClassName)}
          >
            <Dropdown
              trigger={['contextMenu']}
              menu={getMenuProps(board)}
              placement="bottomLeft"
              open={openCtxDropdownId === board.id}
              onOpenChange={(visible) => {
                onContextStateChanged(visible)
                setOpenCtxDropdownId(visible ? board.id : null)

                if (visible) {
                  setOpenBtnDropdownId(null)
                }
              }}
            >
              <SBoardCard
                id={board.id}
                key={board.id}
                className={classNames(selectedTargetClassName, {
                  selected: selectedItemsIds.includes(board.id),
                  'hover-enabled': isItemHoverEnabled,
                  'drag-over': isDragOver,
                  'dragging': isDragging
                })}
                onClick={doubleClickCallback.bind(null, board)}
                onDragOver={(e) => handleDragOver(e, board.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, board.id)}
                draggable={hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled}
                onDragStart={(e) => handleBoardDragStart(e, board)}
                onDragEnd={handleBoardDragEnd}
              >
                <SBoardCardTitle>
                  {board.name}
                </SBoardCardTitle>

                <Dropdown
                  trigger={['hover']}
                  menu={getMenuProps(board)}
                  placement="bottomLeft"
                  open={openBtnDropdownId === board.id}
                  onOpenChange={(visible) => {
                    onContextStateChanged(visible)

                    setOpenBtnDropdownId(visible ? board.id : null)
                    setOpenCtxDropdownId(null)
                  }}
                >
                  <SActionButton
                    onClick={preventPropagation}
                    variant="text"
                    color="default"
                    icon={(
                      <EllipsisOutlined
                        style={{ fontSize: 18 }}
                      />
                    )}
                    style={{ color: 'white', fontSize: 24 }}
                  />
                </Dropdown>

                <SCardOverlay className={classNames(selectedTargetClassName)} />
              </SBoardCard>
            </Dropdown>
          </SBardCardWrapper>
        )
      })}
    </SBoardsGrid>
  )
}

const SBoardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 210px);
  gap: 20px;
  position: relative;
`

const SCardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
`

const SActionButton = styled(Button)`
  && {
    width: 24px;
    height: 24px;
    display: none;
    position: absolute;
    top: 14px;
    right: 16px;
    z-index: 1;
    background-color: #F5F5F5 !important;
    color: black !important;
    

    ul {
      min-width: 186px;
    }

    button {
      width: 24px;
      height: 24px;
    }
    
    &[ant-click-animating-without-extra-node]:after {
      animation: none !important;
    }
  }
`

const SDropdownButton = styled(Dropdown.Button)`
  && {
    display: none;
    position: absolute;
    top: 14px;
    right: 16px;
    width: unset;

    ul {
      min-width: 186px;
    }

    button {
      width: 24px;
      height: 24px;
    }
  }
`

const SBoardCard = styled.div`
  height: 103px;
  background-color: rgba(245, 245, 245, 1);
  border-radius: 8px;
  padding: 16px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1;

  &.hover-enabled:hover {
    ${SCardOverlay} {
      opacity: 1;
    };

    ${SActionButton} {
      display: flex;
    }
  }

  &.selected {
    outline-offset: 2px;
    outline: var(--ant-color-primary-text-active) solid 2px;
    
    ${SCardOverlay} {
      opacity: 1;
      background-color: var(--ant-color-primary-bg-hover);
    };

    ${SDropdownButton} {
      display: none;
    }

    ${SActionButton} {
      display: flex;
    }
  }

  &.drag-over {
    outline: 1px solid #854ECA;
    outline-offset: 2px;
    background-color: rgba(236, 24, 255, 0.05);
    transform: scale(1.02);
  }

  &.dragging {
    opacity: 0.24;

    ${SDropdownButton} {
      display: none;
    }

    ${SActionButton} {
      display: none;
    }
  }

  &[draggable="true"]:active {
    cursor: grabbing;
  }
`

const SBoardCardTitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.88);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 44px;
`

const SBardCardWrapper = styled.div``
